import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { RxPlus } from "react-icons/rx";
import { useMemo, useState } from "react";
import { columns, Meal } from "@/components/nutrition/mealColumns";
import MealDialog from "@/components/nutrition/mealDialog";
import { VirtualizedDataTable } from "@/components/dataTable/VirtualizedDataTable";
import DeleteAlert from "@/components/DeleteAlert";
import { Search, Utensils } from "lucide-react";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import RecipeSelectDialog from "@/components/nutrition/recipeSelectDialog";
import {
  useHasPermission,
  useOrg,
  useUserId,
} from "@/lib/states/session/hooks";
import { errorToast } from "@/lib/utils/ToastUtils";
import { trpc } from "@/lib/trpc/client";
import { patchListCache, removeFromList } from "@trainrr/api-client";
import { canDeleteOrgLibraryItem } from "@/lib/libraryAccess";

export const Route = createFileRoute("/_auth/_dashboard/meals")({
  component: MealList,
});

function MealList() {
  const org = useOrg();
  const userId = useUserId();
  const canManageLibrary = useHasPermission("library.edit");
  const tableColumns = useMemo(
    () => columns({ userId, canManageLibrary }),
    [userId, canManageLibrary],
  );
  const [open, setOpen] = useState(false);
  const [recipeDialogOpen, setRecipeDialogOpen] = useState(false);
  const [meal, setMeal] = useState<Meal | undefined>(undefined);
  const [mealToDelete, setMealToDelete] = useState<Meal | undefined>();

  const { data: meals, isLoading, error } = trpc.meals.list.useQuery(
    undefined,
    { enabled: Boolean(org?.id) },
  );
  const utils = trpc.useUtils();
  const deleteMeal = trpc.meals.delete.useMutation({
    onSuccess: (_data, variables) => {
      patchListCache(utils.meals.list, (old) =>
        removeFromList(old, variables.id),
      );
      setMealToDelete(undefined);
    },
    onError: (mutationError) => {
      errorToast(mutationError);
    },
  });

  if (error) {
    console.error("error fetching meals", error);
  }

  const handleOnClickEdit = (selected: Meal) => {
    if (open) {
      return;
    }

    setMeal(selected);
    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setMeal(undefined);
    setOpen(nextOpen);
  };

  const handleMealDeleted = (selected: Meal) => {
    if (!canDeleteOrgLibraryItem(selected, { userId, canManageLibrary })) {
      return;
    }
    setMealToDelete(selected);
  };

  return (
    <>
      <DeleteAlert
        title={`Delete Meal`}
        description={`Are you sure you want to delete this meal? This action cannot be undone.`}
        open={mealToDelete !== undefined}
        onCancel={() => setMealToDelete(undefined)}
        onDelete={() => {
          if (
            !mealToDelete?.id ||
            !canDeleteOrgLibraryItem(mealToDelete, {
              userId,
              canManageLibrary,
            })
          ) {
            setMealToDelete(undefined);
            return;
          }
          deleteMeal.mutate({ id: mealToDelete.id });
        }}
      />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">
                Meal Library
              </h2>
              <div className="flex items-center gap-2">
                {import.meta.env.MODE === "development" && (
                  <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => setRecipeDialogOpen(true)}
                  >
                    <Search className="h-4 w-4" /> Test Recipe Search
                  </Button>
                )}
                <Button className="gap-2" onClick={() => setOpen(true)}>
                  <RxPlus /> Add New
                </Button>
              </div>
            </div>
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={tableColumns}
              data={(meals as Meal[]) ?? []}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={handleMealDeleted}
              showSearchBar
              stickyHeader
              isLoading={isLoading}
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              virtualizedHeight="100%"
              estimateRowHeight={72}
              emptyView={
                <Empty className="border border-dashed">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Utensils className="h-5 w-5" />
                    </EmptyMedia>
                    <EmptyTitle>No meals yet</EmptyTitle>
                    <EmptyDescription>
                      Save meals to your library to reuse in meal plans.
                    </EmptyDescription>
                  </EmptyHeader>
                  <EmptyContent>
                    <Button className="gap-2" onClick={() => setOpen(true)}>
                      <RxPlus /> Create meal
                    </Button>
                  </EmptyContent>
                </Empty>
              }
            />
          </div>
        </div>
      </div>
      <MealDialog
        open={open}
        onOpenChange={handleOpenChange}
        meal={meal}
      />
      <RecipeSelectDialog
        open={recipeDialogOpen}
        onOpenChange={setRecipeDialogOpen}
      />
    </>
  );
}
